/** Code of the built-in system administrator role — locked from editing. */
export const SYSTEM_ROLE_CODE = 'admin';

/**
 * Role codes that are immutable in the UI (add/remove/save controls are disabled).
 * The `admin` code is the Power User persona; no separate `power_user` code exists in the seed.
 * Source: admin-roles-api spec, task 4.2 notes.
 */
export const IMMUTABLE_ROLE_CODES: readonly string[] = [SYSTEM_ROLE_CODE];

/**
 * Permission codes whose checks receive a `mediaType` context in the API.
 *
 * The list follows the concrete enforcement paths: media-unit read/write/
 * availability services, reservation management, attachment management,
 * cost visibility/history, supplier field visibility on a media unit, and
 * the per-media-type import permission enforced in the worker. Every other
 * permission is checked without a media type, so assigning a media-type
 * scope would not constrain it.
 *
 * Keep this list aligned with the `PolicyService` callers in `apps/api/src/`;
 * it intentionally excludes `media_unit.block_availability` and the supplier
 * management/list permissions. Those operations do not receive a media type,
 * and this UI restriction does not add supplier-list filtering.
 *
 * `reservation.force_release` is intentionally excluded: its write-path
 * asserts (`assertHolderOrForce`, `resolveScopeUserId`, `hasForceReleaseCapability`
 * in `apps/api/src/reservations/reservations.service.ts`) call
 * `PolicyService.assert` with no context, so only a fully-wildcard scope row
 * ever matches — a media-type-scoped grant would render the action visible
 * (the button-visibility resolver in `apps/api/src/offers/availability.helpers.ts`
 * is entity-scoped) but 403 on every invocation.
 *
 * Source: claude review PRR_kwDORYrm6c8AAAABG9sW7Q on PR #879 — the original
 * list omitted several API-enforced codes (media_unit.update_availability,
 * reservation.manage, import). A follow-up review removed
 * reservation.force_release for the reason above.
 */
export const MEDIA_TYPE_ENFORCED_PERMISSION_CODES: readonly string[] = [
  'media_unit.view',
  'media_unit.create',
  'media_unit.edit',
  'media_unit.delete',
  'media_unit.update_availability',
  'reservation.manage',
  'attachment.manage',
  'cost.view',
  'cost.view_history',
  'supplier.view',
  'import',
] as const;

/** Returns true when the media-type scope picker is meaningful for `permissionCode`. */
export function allowMediaTypeScope(permissionCode: string): boolean {
  return MEDIA_TYPE_ENFORCED_PERMISSION_CODES.includes(permissionCode);
}

/**
 * Permission codes where the server actually enforces the `department` scope
 * dimension (via `resolveOfferDepartment`, wired for offer viewing/deletion —
 * see `apps/api/src/policy/offer-department.resolver.ts` and the
 * `rowMatchesContext` helper in `apps/api/src/policy/policy.service.ts`; the
 * same department-vs-wildcard scope-row matching is also applied to
 * `offer.approve_any` by `OfferApprovalService.hasApprovalPermission` in
 * `apps/api/src/offers/offer-approval/offer-approval.service.ts`). For every
 * other permission the server never populates `context.department`, so a
 * department scope grant would be silently no-op. The permission-matrix UI
 * restricts the department-scope picker to these codes only.
 */
export const DEPARTMENT_ENFORCED_PERMISSION_CODES: readonly string[] = [
  'offer.view_all',
  'offer.delete',
  'offer.approve_any',
] as const;

/** Returns true when the department-scope picker should be interactive for `permissionCode`. */
export function allowDepartmentScope(permissionCode: string): boolean {
  return DEPARTMENT_ENFORCED_PERMISSION_CODES.includes(permissionCode);
}
